import { useEffect, useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useLeague } from '@/contexts/LeagueContext';
import { useAuth } from '@/contexts/AuthContext';
import { cn } from '@/lib/utils';
import { resolveDefaultWeek, playoffRoundName, totalPlayoffRounds, playoffRoundIndex } from '@/lib/playoffWeeks';
import { Calendar, ChevronLeft, ChevronRight, Trophy, Crown, Shield } from 'lucide-react';
import { bareDateToDate } from '@/lib/etDate';

interface WeekMatchup {
  id: string;
  week: number;
  home_team_id: string;
  away_team_id: string;
  home_team_name: string;
  away_team_name: string;
  home_score: number;
  away_score: number;
  status: string;
  week_start: string;
  week_end: string;
}

interface StandingEntry {
  team_id: string;
  team_name: string;
  wins: number;
  losses: number;
  total_points: number;
}

interface BracketMatchup {
  id: string;
  round: string;
  team1: { id: string; name: string; seed: number; score: number } | null;
  team2: { id: string; name: string; seed: number; score: number } | null;
  winnerId: string | null;
  status: string;
}

interface BracketRound {
  name: string;
  matchups: BracketMatchup[];
}

const MLBBetaFantasySchedule = () => {
  const { activeLeague, teams } = useLeague();
  const { user } = useAuth();
  const [matchups, setMatchups] = useState<WeekMatchup[]>([]);
  const [loading, setLoading] = useState(true);
  const [currentWeek, setCurrentWeek] = useState(1);
  const [maxWeek, setMaxWeek] = useState(1);
  const [currentPlayoffWeek, setCurrentPlayoffWeek] = useState(0);

  const myTeam = teams.find(t => t.user_id === user?.id);
  const regularSeasonWeeks = activeLeague?.regular_season_weeks || 22;
  const playoffTeamCount = activeLeague?.playoff_teams || 4;

  useEffect(() => {
    if (!activeLeague) { setLoading(false); return; }

    const fetchSchedule = async () => {
      const { data, error } = await supabase
        .from('matchups')
        .select('id, week, home_team_id, away_team_id, home_team_name, away_team_name, home_score, away_score, status, week_start, week_end')
        .eq('league_id', activeLeague.id)
        .order('week');

      if (error) { console.error(error); setLoading(false); return; }

      const mapped = (data || []).map(m => ({
        ...m,
        home_score: Number(m.home_score),
        away_score: Number(m.away_score),
      }));

      setMatchups(mapped);

      const maxW = Math.max(...mapped.map(m => m.week), 1);
      setMaxWeek(maxW);

      const rsWeeks = activeLeague?.regular_season_weeks || 22;
      // Same chain as MLBBetaMatchups, from the shared helper.
      const startWeek = resolveDefaultWeek(mapped);
      // Clamp to regular season for the schedule tab
      setCurrentWeek(Math.min(startWeek, rsWeeks));

      // Initialize playoff week navigator
      const playoffWeeks = [...new Set(mapped.filter(m => m.week > (activeLeague?.regular_season_weeks || 22)).map(m => m.week))].sort((a, b) => a - b);
      if (playoffWeeks.length > 0) {
        const livePlayoff = playoffWeeks.find(w => mapped.some(m => m.week === w && m.status === 'live'));
        setCurrentPlayoffWeek(livePlayoff || playoffWeeks[0]);
      }

      setLoading(false);
    };

    fetchSchedule();
  }, [activeLeague]);

  // Build standings from regular season matchups for seeding
  const buildStandings = (): StandingEntry[] => {
    const regularMatchups = matchups.filter(m => m.week <= regularSeasonWeeks && m.status === 'final');
    const statsMap = new Map<string, StandingEntry>();

    // Initialize from teams
    teams.forEach(t => {
      statsMap.set(t.id, { team_id: t.id, team_name: t.name, wins: 0, losses: 0, total_points: 0 });
    });

    regularMatchups.forEach(m => {
      const home = statsMap.get(m.home_team_id);
      const away = statsMap.get(m.away_team_id);
      if (home) {
        home.total_points += m.home_score;
        if (m.home_score > m.away_score) home.wins++; else home.losses++;
      }
      if (away) {
        away.total_points += m.away_score;
        if (m.away_score > m.home_score) away.wins++; else away.losses++;
      }
    });

    // SAB-903 / SAB-782: honour the tiebreaker the commissioner actually chose.
    //
    // The league wizard has collected category_config.tiebreaker since launch and NOTHING
    // has ever read it -- this sort ranked wins then total points regardless, so the setting
    // was a control that did nothing. Same rule and same wording as NFL (Contest Rules s8).
    //
    // 'head-to-head' is WINS MINUS LOSSES over every decided meeting between the tied teams
    // (Adam's ruling 2026-08-28: record, not win percentage). Teams that never met, split
    // level, or sit in a circular result all score 0 and fall through to total points, which
    // is what the rules page discloses. Legacy value 'none' and any unrecognised value fall
    // back to total points -- the default -- rather than leaving the order undefined.
    const tiebreaker = (activeLeague as any)?.category_config?.tiebreaker;

    // head-to-head is only meaningful BETWEEN tied teams, so it is computed per tie group
    // rather than as a season-wide scalar.
    const h2hWithin = (group: StandingEntry[]): Map<string, number> => {
      const ids = new Set(group.map(g => g.team_id));
      const rec = new Map<string, number>(group.map(g => [g.team_id, 0]));
      regularMatchups.forEach(m => {
        if (!ids.has(m.home_team_id) || !ids.has(m.away_team_id)) return;
        if (m.home_score === m.away_score) return;
        const winner = m.home_score > m.away_score ? m.home_team_id : m.away_team_id;
        const loser = m.home_score > m.away_score ? m.away_team_id : m.home_team_id;
        rec.set(winner, (rec.get(winner) ?? 0) + 1);
        rec.set(loser, (rec.get(loser) ?? 0) - 1);
      });
      return rec;
    };

    const byWins = Array.from(statsMap.values()).sort((a, b) => b.wins - a.wins);
    if (tiebreaker !== 'head-to-head') {
      return byWins.sort((a, b) => {
        if (b.wins !== a.wins) return b.wins - a.wins;
        return b.total_points - a.total_points;
      });
    }

    const out: StandingEntry[] = [];
    let i = 0;
    while (i < byWins.length) {
      let j = i;
      while (j < byWins.length && byWins[j].wins === byWins[i].wins) j++;
      const group = byWins.slice(i, j);
      if (group.length > 1) {
        const rec = h2hWithin(group);
        group.sort((a, b) => {
          const d = (rec.get(b.team_id) ?? 0) - (rec.get(a.team_id) ?? 0);
          return d !== 0 ? d : b.total_points - a.total_points;
        });
      }
      out.push(...group);
      i = j;
    }
    return out;
  };

  // Check if regular season is complete (all regular season matchups are final)
  const isRegularSeasonComplete = (): boolean => {
    const config = activeLeague?.category_config as any;
    if (config?.regular_season_end_date) {
      const endDate = new Date(config.regular_season_end_date + 'T23:59:59Z');
      return new Date() > endDate;
    }
    // Fallback: check if all regular season matchups are final
    const regularMatchups = matchups.filter(m => m.week <= regularSeasonWeeks);
    return regularMatchups.length > 0 && regularMatchups.every(m => m.status === 'final');
  };

  // Build bracket from playoff matchups
  const buildBracket = (): BracketRound[] => {
    const seasonComplete = isRegularSeasonComplete();
    const standings = seasonComplete ? buildStandings() : [];
    const playoffMatchups = matchups.filter(m => m.week > regularSeasonWeeks);

    if (!seasonComplete || playoffMatchups.length === 0) {
      // Regular season not over or no playoff matchups yet — show the PROJECTED bracket.
      //
      // The previous version paired highSeed = i+1 against lowSeed = playoffTeamCount - i,
      // which is only correct when playoffTeamCount is a power of two. For a 6-team bracket
      // it drew 1v6 / 2v5 / 3v4 — no byes, three "semifinals", and a round list of
      // Semifinals -> Championship -> Finals. F6P is a real 6-team league and its actual
      // round one is 3v6 and 4v5 with seeds 1 and 2 on bye, so the projection contradicted
      // the matchups the league was about to play (reported by Joe Bond, 2026-08-24).
      //
      // General rule: pad up to the next power of two, and the padding IS the byes.
      //   6 teams -> bracket of 8 -> 2 byes -> round one is seeds 3..6 as 3v6, 4v5
      //   4 teams -> bracket of 4 -> 0 byes -> round one is 1v4, 2v3   (unchanged)
      //   8 teams -> bracket of 8 -> 0 byes -> 1v8, 2v7, 3v6, 4v5      (unchanged)
      const n = Math.max(playoffTeamCount, 2);
      const totalRounds = totalPlayoffRounds(n);
      const bracketSize = 2 ** totalRounds;
      const byes = bracketSize - n;
      const firstRoundGames = (n - byes) / 2;

      // Name rounds from the END, so the last is always Championship regardless of depth.
      const seedEntry = (seed: number) => {
        const t = seasonComplete ? standings[seed - 1] : null;
        return t
          ? { id: t.team_id, name: t.team_name, seed, score: 0 }
          : { id: '', name: `#${seed} TBD`, seed, score: 0 };
      };

      const rounds: BracketRound[] = [];

      // ── Round 1: only the teams WITHOUT a bye, paired highest vs lowest ──
      const roundOneName = playoffRoundName(0, totalRounds);
      const roundOne: BracketMatchup[] = [];
      for (let i = 0; i < firstRoundGames; i++) {
        const highSeed = byes + 1 + i;
        const lowSeed = n - i;
        roundOne.push({
          id: `proj-r1-${i}`,
          round: roundOneName,
          team1: seedEntry(highSeed),
          team2: seedEntry(lowSeed),
          winnerId: null,
          status: 'projected',
        });
      }
      rounds.push({ name: roundOneName, matchups: roundOne });

      // ── Round 2: bye seeds enter here, against round-one winners ──
      // Highest bye seed faces the LAST round-one game, so seed 1 meets the winner of the
      // lowest-ranked pairing (6 teams: 1 vs winner 4/5, 2 vs winner 3/6).
      if (byes > 0 && totalRounds >= 2) {
        const roundTwoName = playoffRoundName(1, totalRounds);
        const roundTwo: BracketMatchup[] = [];
        for (let k = 1; k <= byes; k++) {
          const opponentGame = roundOne[firstRoundGames - k];
          const opponentLabel = opponentGame
            ? `Winner ${opponentGame.team1?.seed}/${opponentGame.team2?.seed}`
            : 'TBD';
          roundTwo.push({
            id: `proj-r2-${k}`,
            round: roundTwoName,
            team1: seedEntry(k),
            team2: { id: '', name: opponentLabel, seed: 0, score: 0 },
            winnerId: null,
            status: 'projected',
          });
        }
        rounds.push({ name: roundTwoName, matchups: roundTwo });
      }

      // ── Remaining rounds: fully TBD ──
      for (let idx = rounds.length; idx < totalRounds; idx++) {
        const name = playoffRoundName(idx, totalRounds);
        const games = bracketSize / 2 ** (idx + 1);
        rounds.push({
          name,
          matchups: Array.from({ length: games }, (_, g) => ({
            id: `proj-r${idx + 1}-${g}`,
            round: name,
            team1: { id: '', name: 'TBD', seed: 0, score: 0 },
            team2: { id: '', name: 'TBD', seed: 0, score: 0 },
            winnerId: null,
            status: 'projected' as const,
          })),
        });
      }

      return rounds;
    }

    // Build from real playoff matchups
    const seedMap = new Map<string, number>();
    if (seasonComplete) {
      standings.slice(0, playoffTeamCount).forEach((s, i) => seedMap.set(s.team_id, i + 1));
    }

    const roundGroups = new Map<number, WeekMatchup[]>();
    playoffMatchups.forEach(m => {
      const list = roundGroups.get(m.week) || [];
      list.push(m);
      roundGroups.set(m.week, list);
    });

    const sortedWeeks = Array.from(roundGroups.keys()).sort((a, b) => a - b);

    // Name rounds from the END, so the last playoff week is always the Championship.
    // Previously a fixed ['Semifinals','Championship','Finals'] indexed by position, which
    // mislabels any bracket that is not exactly two rounds: F6P's three playoff weeks came
    // out as Semifinals -> Championship -> Finals, so round one was called the semifinal and
    // the actual title game came AFTER something already named "Championship".
    // Same defect as the projected bracket above, on the path users actually see once real
    // playoff matchups exist.
    // Round count comes from the league config, NOT from how many playoff weeks have rows.
    // Deriving it from sortedWeeks.length made F6P's week 21 read "Championship" (with one
    // round, index 0 is the last round) and would have relabelled it every week as 22 and
    // 23 were created.
    const totalRounds = totalPlayoffRounds(playoffTeamCount);
    // Byes for a non-power-of-two bracket: 6 teams -> bracket of 8 -> seeds 1 and 2 sit out
    // round one. They appear nowhere in the matchup rows, so the bracket has to add them or
    // the top two seeds vanish from the playoffs entirely.
    const realBracketSize = 2 ** totalRounds;
    const realByes = Math.max(0, realBracketSize - playoffTeamCount);
    return sortedWeeks.map((week) => {
      const weekMatchups = roundGroups.get(week) || [];
      const idx = playoffRoundIndex(week, regularSeasonWeeks);
      const name = playoffRoundName(idx, totalRounds);

      return {
        name,
        matchups: weekMatchups.map(m => {
          const homeWins = m.home_score > m.away_score;
          const isFinal = m.status === 'final';
          return {
            id: m.id,
            round: name,
            team1: {
              id: m.home_team_id,
              name: m.home_team_name,
              seed: seedMap.get(m.home_team_id) || 0,
              score: m.home_score,
            },
            team2: {
              id: m.away_team_id,
              name: m.away_team_name,
              seed: seedMap.get(m.away_team_id) || 0,
              score: m.away_score,
            },
            winnerId: isFinal ? (homeWins ? m.home_team_id : m.away_team_id) : null,
            status: m.status,
          };
        }).concat(
          // Seeds on bye play no matchup, so they exist in no row. Without this the top
          // seeds disappear from the bracket entirely — F6P showed two games and no sign
          // that F6P_Joe's Team and Stinks had advanced (reported by Joe, 2026-08-24).
          idx === 0 && realByes > 0
            ? standings.slice(0, realByes).map((s, i) => ({
                id: `bye-${s.team_id}`,
                round: name,
                team1: { id: s.team_id, name: s.team_name, seed: i + 1, score: 0 },
                team2: { id: '', name: 'BYE — advances', seed: 0, score: 0 },
                winnerId: s.team_id,
                status: 'bye',
              }))
            : [],
        ),
      };
    });
  };

  if (loading) {
    return (
      <div className="space-y-3 pb-20 md:pb-0">
        <Skeleton className="h-8 w-48" />
        {Array.from({ length: 4 }).map((_, i) => <Skeleton key={i} className="h-20" />)}
      </div>
    );
  }

  if (matchups.length === 0) {
    return (
      <Card className="bg-card border-border">
        <CardContent className="p-8 text-center text-muted-foreground">
          <p className="font-medium text-foreground">No schedule yet</p>
          <p className="text-sm mt-1">The commissioner needs to generate the matchup schedule in Settings.</p>
        </CardContent>
      </Card>
    );
  }

  const weekMatchups = matchups.filter(m => m.week === currentWeek);
  const weekStart = weekMatchups[0]?.week_start;
  const weekEnd = weekMatchups[0]?.week_end;

  const myMatchup = myTeam
    ? weekMatchups.find(m => m.home_team_id === myTeam.id || m.away_team_id === myTeam.id)
    : null;

  const formatDate = (d: string) => {
    const date = bareDateToDate(d);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  const bracket = buildBracket();
  const standings = buildStandings();
  const playoffContenders = standings.slice(0, playoffTeamCount);

  return (
    <div className="space-y-4 pb-20 md:pb-0">
      <div className="flex items-center justify-between">
        <h1 className="text-xl font-bold font-heading text-foreground flex items-center gap-2">
          <Calendar className="w-5 h-5 text-primary" />
          Fantasy Schedule
        </h1>
      </div>

      <Tabs defaultValue="schedule">
        <TabsList className="w-full grid grid-cols-2">
          <TabsTrigger value="schedule" className="gap-1.5">
            <Calendar className="w-4 h-4" />Schedule
          </TabsTrigger>
          <TabsTrigger value="playoffs" className="gap-1.5">
            <Trophy className="w-4 h-4" />Playoffs
          </TabsTrigger>
        </TabsList>

        {/* Weekly Schedule Tab */}
        <TabsContent value="schedule" className="space-y-4 mt-4">
          {/* Week navigator */}
          <div className="flex items-center justify-between bg-muted/30 rounded-lg px-4 py-2.5">
            <button onClick={() => setCurrentWeek(w => Math.max(1, w - 1))} disabled={currentWeek <= 1}
              className="p-1 rounded hover:bg-muted transition-colors disabled:opacity-30">
              <ChevronLeft className="w-5 h-5" />
            </button>
            <div className="text-center">
              <p className="text-sm font-bold text-foreground">
                Week {currentWeek}
              </p>
              {weekStart && weekEnd && (
                <p className="text-[11px] text-muted-foreground">{formatDate(weekStart)} – {formatDate(weekEnd)}</p>
              )}
            </div>
            <button onClick={() => setCurrentWeek(w => Math.min(regularSeasonWeeks, w + 1))} disabled={currentWeek >= regularSeasonWeeks}
              className="p-1 rounded hover:bg-muted transition-colors disabled:opacity-30">
              <ChevronRight className="w-5 h-5" />
            </button>
          </div>

          {/* My matchup */}
          {myMatchup && (
            <Card className="bg-primary/5 border-primary/20 overflow-hidden">
              <CardContent className="p-0">
                <div className="px-4 py-1.5 bg-primary/10">
                  <span className="text-[10px] font-semibold uppercase tracking-wider text-primary">Your Matchup</span>
                </div>
                <MatchupRow matchup={myMatchup} myTeamId={myTeam?.id} highlight />
              </CardContent>
            </Card>
          )}

          {/* All matchups */}
          <Card className="bg-card border-border overflow-hidden">
            <div className="px-4 py-2 bg-muted/30 border-b border-border">
              <span className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                All Matchups ({weekMatchups.length})
              </span>
            </div>
            {weekMatchups.map((m) => (
              <MatchupRow key={m.id} matchup={m} myTeamId={myTeam?.id} />
            ))}
          </Card>

          {/* Week selector with arrows */}
          <div className="flex items-center gap-1.5 justify-center">
            <button
              onClick={() => setCurrentWeek(w => Math.max(1, w - 1))}
              disabled={currentWeek <= 1}
              className="p-1.5 rounded bg-muted text-foreground hover:bg-muted/80 disabled:opacity-30 shrink-0"
            >
              <ChevronLeft className="w-4 h-4" />
            </button>
            <div className="flex gap-1 overflow-x-auto no-scrollbar py-1 px-0.5">
              {Array.from({ length: regularSeasonWeeks }, (_, i) => i + 1).map(w => {
                const wMatchups = matchups.filter(m => m.week === w);
                const hasLive = wMatchups.some(m => m.status === 'live');
                const allFinal = wMatchups.length > 0 && wMatchups.every(m => m.status === 'final');

                return (
                  <button key={w} onClick={() => setCurrentWeek(w)}
                    className={cn(
                      "w-8 h-8 rounded text-xs font-medium transition-colors shrink-0",
                      w === currentWeek && "bg-primary text-primary-foreground",
                      w !== currentWeek && allFinal && "bg-muted text-muted-foreground",
                      w !== currentWeek && hasLive && "bg-destructive/20 text-destructive",
                      w !== currentWeek && !allFinal && !hasLive && "bg-background border border-border text-muted-foreground hover:border-primary/50",
                    )}>
                    {w}
                  </button>
                );
              })}
            </div>
            <button
              onClick={() => setCurrentWeek(w => Math.min(regularSeasonWeeks, w + 1))}
              disabled={currentWeek >= regularSeasonWeeks}
              className="p-1.5 rounded bg-muted text-foreground hover:bg-muted/80 disabled:opacity-30 shrink-0"
            >
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>
        </TabsContent>

        {/* Playoffs Tab */}
        <TabsContent value="playoffs" className="space-y-6 mt-4">
          {!isRegularSeasonComplete() ? (
            /* Regular season still in progress — hide all playoff details */
            <Card className="bg-card border-border">
              <CardContent className="p-8 text-center">
                <Trophy className="w-12 h-12 mx-auto mb-3 text-primary opacity-40" />
                <p className="font-heading font-bold text-foreground">Playoff bracket will be revealed when the regular season ends</p>
                <p className="text-sm text-muted-foreground mt-1">
                  {(() => {
                    const config = activeLeague?.category_config as any;
                    if (config?.regular_season_end_date) {
                      const d = bareDateToDate(config.regular_season_end_date);
                      return d.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
                    }
                    return null;
                  })()}
                </p>
                <p className="text-xs text-muted-foreground mt-3">Top {playoffTeamCount} teams will qualify for the playoffs</p>
              </CardContent>
            </Card>
          ) : (
            /* Regular season complete — show bracket + matchup schedule */
            <>
              {/* Bracket */}
              <Card className="bg-card border-border">
                <CardHeader className="pb-3">
                  <CardTitle className="flex items-center gap-2 text-base">
                    <Trophy className="w-5 h-5 text-primary" />
                    Playoff Bracket
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {bracket.length === 0 ? (
                    <div className="text-center text-muted-foreground py-8">
                      <Trophy className="w-10 h-10 mx-auto mb-2 opacity-40" />
                      <p className="text-sm">Bracket will appear once standings are established</p>
                    </div>
                  ) : (
                    <div className="flex flex-col lg:flex-row gap-8 justify-center items-start">
                      {bracket.map((round, roundIndex) => (
                        <div key={round.name} className="space-y-4 w-full lg:w-auto">
                          <h3 className="text-center font-heading font-bold text-sm uppercase tracking-wider text-muted-foreground">
                            {round.name}
                          </h3>
                          <div className="space-y-4 flex flex-col justify-center" style={{ minHeight: roundIndex > 0 ? `${bracket[0].matchups.length * 120}px` : undefined }}>
                            {round.matchups.map((matchup) => (
                              <BracketCard key={matchup.id} matchup={matchup} />
                            ))}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Playoff Week Matchups */}
              {(() => {
                const playoffWeeks = [...new Set(matchups.filter(m => m.week > regularSeasonWeeks).map(m => m.week))].sort((a, b) => a - b);
                if (playoffWeeks.length === 0) return null;
                const pw = currentPlayoffWeek || playoffWeeks[0];
                const pwMatchups = matchups.filter(m => m.week === pw);
                const pwStart = pwMatchups[0]?.week_start;
                const pwEnd = pwMatchups[0]?.week_end;
                const roundIdx = playoffRoundIndex(pw, regularSeasonWeeks);
                const roundName = playoffRoundName(roundIdx, totalPlayoffRounds(playoffTeamCount));

                return (
                  <Card className="bg-card border-border overflow-hidden">
                    <div className="flex items-center justify-between px-4 py-2.5 bg-muted/30 border-b border-border">
                      <button onClick={() => setCurrentPlayoffWeek(playoffWeeks[Math.max(0, playoffWeeks.indexOf(pw) - 1)])}
                        disabled={playoffWeeks.indexOf(pw) <= 0}
                        className="p-1 rounded hover:bg-muted transition-colors disabled:opacity-30">
                        <ChevronLeft className="w-4 h-4" />
                      </button>
                      <div className="text-center">
                        <p className="text-sm font-bold text-foreground">{roundName}</p>
                        {pwStart && pwEnd && (
                          <p className="text-[11px] text-muted-foreground">{formatDate(pwStart)} – {formatDate(pwEnd)}</p>
                        )}
                      </div>
                      <button onClick={() => setCurrentPlayoffWeek(playoffWeeks[Math.min(playoffWeeks.length - 1, playoffWeeks.indexOf(pw) + 1)])}
                        disabled={playoffWeeks.indexOf(pw) >= playoffWeeks.length - 1}
                        className="p-1 rounded hover:bg-muted transition-colors disabled:opacity-30">
                        <ChevronRight className="w-4 h-4" />
                      </button>
                    </div>
                    {pwMatchups.map((m) => (
                      <MatchupRow key={m.id} matchup={m} myTeamId={myTeam?.id} />
                    ))}
                  </Card>
                );
              })()}
            </>
          )}

          {/* Playoff Contenders */}
          <Card className="bg-card border-border">
            <CardHeader className="pb-3">
              <CardTitle className="text-base">Playoff Contenders</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                {playoffContenders.map((team, i) => (
                  <div key={team.team_id} className="flex items-center gap-3 p-3 bg-muted/30 rounded-lg">
                    <span className={cn(
                      "w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold",
                      i === 0 && "bg-primary text-primary-foreground",
                      i === 1 && "bg-muted-foreground/80 text-background",
                      i === 2 && "bg-amber-700 text-background",
                      i >= 3 && "bg-muted text-muted-foreground"
                    )}>
                      {i + 1}
                    </span>
                    <div className="flex-1 min-w-0">
                      <span className="font-medium text-sm">{team.team_name}</span>
                      <p className="text-xs text-muted-foreground">{team.wins}-{team.losses}</p>
                    </div>
                    <div className="text-right">
                      <p className="text-sm font-bold font-heading">{team.total_points.toFixed(1)}</p>
                      <p className="text-[10px] text-muted-foreground">Total Pts</p>
                    </div>
                    <Badge variant="outline" className="text-[10px]">
                      <Shield className="w-3 h-3 mr-1" />#{i + 1}
                    </Badge>
                  </div>
                ))}
                {playoffContenders.length === 0 && (
                  <p className="text-sm text-muted-foreground text-center py-4">No standings data yet</p>
                )}
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

/* ── Bracket Card ──────────────────────────────────────── */
const BracketCard = ({ matchup }: { matchup: BracketMatchup }) => {
  const isProjected = matchup.status === 'projected';
  const isFinal = matchup.status === 'final';
  const isLive = matchup.status === 'live' || matchup.status === 'in_progress';

  return (
    <div className={cn(
      "bg-muted/30 rounded-lg border min-w-[220px]",
      isProjected && "border-dashed border-border/60",
      isLive && "border-destructive/50 shadow-sm shadow-destructive/10",
      isFinal && "border-primary/30",
      !isProjected && !isLive && !isFinal && "border-border",
    )}>
      {/* Status */}
      <div className="px-3 py-1.5 flex items-center justify-between border-b border-border/30">
        <span className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold">
          {matchup.round}
        </span>
        <Badge className={cn(
          "text-[9px] h-4 px-1.5",
          isLive && "bg-destructive text-destructive-foreground animate-pulse",
          isFinal && "bg-muted text-muted-foreground",
          isProjected && "bg-muted/50 text-muted-foreground",
          !isLive && !isFinal && !isProjected && "bg-muted text-muted-foreground",
        )}>
          {isLive ? '● LIVE' : isFinal ? 'FINAL' : isProjected ? 'PROJECTED' : 'SCHED'}
        </Badge>
      </div>

      {/* Team 1 */}
      <div className={cn(
        "flex items-center justify-between px-3 py-2.5",
        matchup.winnerId === matchup.team1?.id && "bg-primary/10",
      )}>
        {matchup.team1 ? (
          <>
            <div className="flex items-center gap-2 min-w-0">
              <span className="w-5 h-5 rounded-full bg-primary text-primary-foreground text-[10px] flex items-center justify-center font-bold shrink-0">
                {matchup.team1.seed || '?'}
              </span>
              <span className={cn(
                "text-sm truncate max-w-[130px]",
                matchup.winnerId === matchup.team1.id ? "font-bold text-foreground" : "font-medium",
              )}>
                {matchup.team1.name}
              </span>
              {matchup.winnerId === matchup.team1.id && <Crown className="w-3.5 h-3.5 text-primary shrink-0" />}
            </div>
            <span className="font-heading font-bold text-sm ml-2">
              {isFinal || isLive ? matchup.team1.score.toFixed(1) : '–'}
            </span>
          </>
        ) : (
          <span className="text-sm text-muted-foreground italic">TBD</span>
        )}
      </div>

      <div className="h-px bg-border/50 mx-3" />

      {/* Team 2 */}
      <div className={cn(
        "flex items-center justify-between px-3 py-2.5",
        matchup.winnerId === matchup.team2?.id && "bg-primary/10",
      )}>
        {matchup.team2 ? (
          <>
            <div className="flex items-center gap-2 min-w-0">
              <span className="w-5 h-5 rounded-full bg-primary text-primary-foreground text-[10px] flex items-center justify-center font-bold shrink-0">
                {matchup.team2.seed || '?'}
              </span>
              <span className={cn(
                "text-sm truncate max-w-[130px]",
                matchup.winnerId === matchup.team2.id ? "font-bold text-foreground" : "font-medium",
              )}>
                {matchup.team2.name}
              </span>
              {matchup.winnerId === matchup.team2.id && <Crown className="w-3.5 h-3.5 text-primary shrink-0" />}
            </div>
            <span className="font-heading font-bold text-sm ml-2">
              {isFinal || isLive ? matchup.team2.score.toFixed(1) : '–'}
            </span>
          </>
        ) : (
          <span className="text-sm text-muted-foreground italic">TBD</span>
        )}
      </div>
    </div>
  );
};

/* ── Matchup Row (schedule tab) ────────────────────────── */
const MatchupRow = ({
  matchup: m,
  myTeamId,
  highlight,
}: {
  matchup: WeekMatchup;
  myTeamId?: string;
  highlight?: boolean;
}) => {
  const homeWins = m.home_score > m.away_score;
  const awayWins = m.away_score > m.home_score;
  const isMyHome = m.home_team_id === myTeamId;
  const isMyAway = m.away_team_id === myTeamId;

  return (
    <div className={cn(
      "flex items-center px-4 py-3 border-t border-border/30 first:border-t-0",
      (isMyHome || isMyAway) && !highlight && "bg-primary/5",
    )}>
      <div className="flex-1 min-w-0">
        <span className={cn(
          "text-sm font-medium block truncate",
          awayWins ? "text-foreground font-semibold" : "text-muted-foreground",
          isMyAway && "text-primary",
        )}>{m.away_team_name}</span>
      </div>
      <div className="flex items-center gap-1.5 px-2 shrink-0">
        <span className={cn("text-lg font-bold font-heading min-w-[28px] text-right", awayWins ? "text-primary" : "text-foreground")}>
          {m.away_score || 0}
        </span>
        <Badge className={cn(
          "text-[9px] px-1.5 min-w-[40px] justify-center",
          m.status === 'live' && "bg-destructive text-destructive-foreground animate-pulse",
          m.status === 'final' && "bg-muted text-muted-foreground",
          m.status === 'scheduled' && "bg-muted/50 text-muted-foreground",
        )}>
          {m.status === 'live' ? 'LIVE' : m.status === 'final' ? 'FINAL' : 'SCH'}
        </Badge>
        <span className={cn("text-lg font-bold font-heading min-w-[28px] text-left", homeWins ? "text-primary" : "text-foreground")}>
          {m.home_score || 0}
        </span>
      </div>
      <div className="flex-1 min-w-0 text-right">
        <span className={cn(
          "text-sm font-medium block truncate",
          homeWins ? "text-foreground font-semibold" : "text-muted-foreground",
          isMyHome && "text-primary",
        )}>{m.home_team_name}</span>
      </div>
    </div>
  );
};

export default MLBBetaFantasySchedule;
